import { OhCase } from "../model";
import { TaskModel, TaskTypes } from "../task/taskActions/models";
import { toast } from "../toasts";

const getPrecedingTask = (updateWorkAdjustmentPrecedingTaskId: string, waCase: OhCase, referralCase: OhCase): TaskModel => {
    if (updateWorkAdjustmentPrecedingTaskId && waCase && referralCase) {
        // The preceding task should be found on the Work Adjustment case
        // ("InitialWorkAdjustmentDetailsTask" or "ManagerToProcessUpdateWorkAdjustmentQuestionnaireTask")
        return waCase?.tasks?.find(t => t.id === updateWorkAdjustmentPrecedingTaskId)
            // or on the parent Referral case (WriteReportReferralTask)
            ?? referralCase?.tasks?.find(t => t.id === updateWorkAdjustmentPrecedingTaskId);
    }
    return null;
};

// Root task of "ManagerToProcessUpdateWorkAdjustmentQuestionnaireTask" is either "InitialWorkAdjustmentDetailsTask" (to be found on "Work Adjustment" case)
// or "WriteReportReferralTask" (to be found on parent "Referral" case) if it's re-referral path
const getRootTask = (updateWorkAdjustmentPrecedingTask: TaskModel, waCase: OhCase, referralCase: OhCase): TaskModel => {
    const precedingTaskId = updateWorkAdjustmentPrecedingTask?.precedingTaskId;
    if (precedingTaskId && waCase && referralCase) {
        const rootWorkAdjustmentTask = waCase.tasks?.find(t => t.id === precedingTaskId);
        if (rootWorkAdjustmentTask) {
            const rootWorkAdjustmentTaskType = rootWorkAdjustmentTask.type;
            switch (rootWorkAdjustmentTaskType) {
                case TaskTypes.ManagerToProcessUpdateWorkAdjustmentQuestionnaireTask:
                    return getRootTask(rootWorkAdjustmentTask, waCase, referralCase);
                case TaskTypes.InitialWorkAdjustmentDetailsTask:
                    return rootWorkAdjustmentTask;
                default:
                    toast.error(`${TaskTypes.InitialWorkAdjustmentDetailsTask} has not been found`);
                    return null;
            }
        } else {
            // Re-referral path
            const writeReferralReportTask = referralCase.tasks?.find(t => t.id === precedingTaskId);
            return writeReferralReportTask;
        }
    }
    toast.error(`Task with id ${updateWorkAdjustmentPrecedingTask?.id} has empty precedingTaskId`);
    return null;
};

const getWriteReferralReportTaskId = (updateWorkAdjustmentPrecedingTask: TaskModel, waCase: OhCase, referralCase: OhCase): string => {
    if (updateWorkAdjustmentPrecedingTask) {
        const precedingTaskType = updateWorkAdjustmentPrecedingTask.type;
        switch (precedingTaskType) {
            case TaskTypes.InitialWorkAdjustmentDetailsTask:
                return updateWorkAdjustmentPrecedingTask.precedingTaskId;
            case TaskTypes.WriteReportReferralTask:
                return updateWorkAdjustmentPrecedingTask.id;
            case TaskTypes.ManagerToProcessUpdateWorkAdjustmentQuestionnaireTask:
                const rootTask = getRootTask(updateWorkAdjustmentPrecedingTask, waCase, referralCase);
                const rootTaskType = rootTask?.type;
                switch (rootTaskType) {
                    case TaskTypes.InitialWorkAdjustmentDetailsTask:
                        return rootTask?.precedingTaskId;
                    case TaskTypes.WriteReportReferralTask:
                        return rootTask?.id;
                    default:
                        toast.error("Root task type is of incorrect type");
                        return null;
                }
            default:
                toast.error("Preceding task type is of incorrect type");
                return null;
        }
    }
    return null;
};

export const useParentWriteReferralReportTaskIdForUpdateQuestionnaire = (
    updateWorkAdjustmentTask: TaskModel,
    workAdjustmentCase: OhCase,
    parentReferralCase: OhCase
) => {
    const precedingTaskId = updateWorkAdjustmentTask?.precedingTaskId;
    const precedingTask = getPrecedingTask(precedingTaskId, workAdjustmentCase, parentReferralCase);
    const writeReferralReportTaskId = getWriteReferralReportTaskId(precedingTask, workAdjustmentCase, parentReferralCase);
    return writeReferralReportTaskId;
};
